import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { SHOP_ADMIN_ROLES } from '@/types/auth';
import type { UserType } from '@/types/auth';

type AdminArea = 'platform' | 'shop';

const AREAS: Record<
  AdminArea,
  { roles: UserType[]; label: string; explanation: string; home: string }
> = {
  platform: {
    roles: ['admin'],
    label: "Administration de la plateforme",
    explanation:
      "Cet espace gère les clubs et les comptes. Un compte boutique n'y a pas accès : il administre le catalogue et les commandes.",
    home: '/admin',
  },
  shop: {
    roles: SHOP_ADMIN_ROLES,
    label: 'Administration de la boutique',
    explanation: "Cet espace gère le catalogue et les commandes.",
    home: '/admin/boutique',
  },
};

/**
 * Garde des espaces d'administration.
 *
 * Deux refus distincts, volontairement : un visiteur non connecté est renvoyé
 * vers la connexion admin, tandis qu'un utilisateur déjà connecté mais sans le
 * rôle voit un message explicite. Le rediriger vers un formulaire de connexion
 * alors qu'il est déjà authentifié ne lui dirait rien de ce qui bloque.
 *
 * L'admin boutique qui tombe sur l'espace plateforme est renvoyé vers le sien
 * plutôt que sermonné : il n'a rien fait de mal, il s'est juste trompé de porte.
 *
 * Ce contrôle est une commodité d'interface : l'autorisation réelle est vérifiée
 * par le back-end sur chaque écriture, à partir du rôle porté par le jeton.
 */
export default function AdminRoute({ area = 'platform' }: { area?: AdminArea }) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  const { roles, label, explanation } = AREAS[area];

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  const userType = user?.user_type;

  if (!userType || !roles.includes(userType)) {
    // Un admin boutique a bien un espace : autant l'y conduire.
    if (area === 'platform' && userType === 'shop_admin') {
      return <Navigate to="/admin/boutique" replace />;
    }

    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border rounded-xl p-6 text-center">
          <ShieldAlert className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900">Espace réservé</h1>
          <p className="text-sm text-gray-600 mt-2">
            Le compte <strong>{user?.email}</strong> n'a pas le rôle requis pour{' '}
            {label.toLowerCase()}.
          </p>
          <p className="text-sm text-gray-600 mt-2">{explanation}</p>
          <p className="text-xs text-gray-500 mt-3">
            Si ton compte vient d'être promu, déconnecte-toi et reconnecte-toi : le rôle est
            inscrit dans le jeton de session au moment de la connexion.
          </p>
          <div className="flex gap-2 justify-center mt-5">
            <a
              href="/admin/login"
              className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm hover:bg-slate-900"
            >
              Changer de compte
            </a>
            <a
              href="/feed"
              className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-50"
            >
              Retour au site
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
